<script setup>
// Fase 2: dashboard com visibilidade de blocos filtrada por voip_business_types.
// Cada bloco declara pra quais tipos aparece. Se o usuario marcou 'misto',
// tudo aparece. Caso contrario, blocos que batem com QUALQUER tipo marcado.
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import VoipAnalyticsAPI from 'dashboard/api/voipAnalytics';
import { useShellPeriod } from 'dashboard/routes/dashboard/settings/reports/composables/useShellPeriod';
import VoipSettingsAPI from 'dashboard/api/voipSettings';
import { useStore } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { formatarCentavos } from 'dashboard/helper/formatCurrency';
import {
  TARGET_FIELDS,
  hasAnyTarget,
  targetDraftFromKpis,
  targetPayloadFromDraft,
} from './helpers/monthlyTargets';

// SiteUp (casa dos relatorios): a mesma tela e a aba "Ligacoes" de Relatorios.
// Embutida, a pagina em volta ja tem titulo e rolagem: a tela entrega so o
// conteudo, sem segundo cabecalho e sem segundo scroll.
defineProps({
  embedded: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const route = useRoute();
const { t } = useI18n();
// Configurações de ligações só abre para administrador: link para lá some
// para quem não tem acesso e vira "peça ao administrador".
const { isAdmin } = useAdmin();

const filters = ref({ from: defaultFrom(), to: '' });
const funnel = ref(null);
const financial = ref(null);
const byOrigin = ref([]);
const byOriginTotal = computed(() =>
  byOrigin.value.reduce((total, r) => total + r.leads, 0)
);
const byAgent = ref([]);
const daily = ref([]);
const distance = ref([]);
const targets = ref(null);
const callsPerLead = ref(null);
const attendance = ref(null);
const leadSpeed = ref(null);
const fcr = ref(null);
const loading = ref(false);
const loadError = ref(null);
// Grupo que falhou fica null e marcado aqui: card com 0 parecia dado real.
const funnelFailed = ref(false);
const financialFailed = ref(false);
// Filtro trocado rápido: resposta velha não sobrescreve a nova.
let loadRequestId = 0;
let compareRequestId = 0;
const businessTypes = ref(['misto']);
// SiteUP CRM (relatorios r2, item B): `pct: 100` como valor inicial e como
// fallback de erro fazia telefonia mal configurada, ou nao verificada, aparecer
// como configuracao completa (a secao de aviso so abre com pct < 100). Ausencia
// de medida agora e `pct: null` com `failed`, e a tela diz que nao conseguiu
// verificar em vez de calar.
const checklist = ref({
  items: [],
  done: 0,
  total: 0,
  pct: null,
  failed: false,
});
const checklistIncomplete = computed(
  () =>
    checklist.value.items.length &&
    checklist.value.pct !== null &&
    checklist.value.pct < 100
);
const checklistCollapsed = ref(false);
// Phase 2.5 H: comparacao vs periodo anterior
const compareEnabled = ref(false);

// Na casa dos relatorios o periodo (e o "Comparar com o periodo anterior") vem
// do seletor unico da casca; as datas digitadas em dd/mm ficam so na rota
// propria de Ligacoes. `filters` segue em ISO, como o backend espera.
const shellPeriod = useShellPeriod(next => {
  filters.value = { ...filters.value, from: next.startIso, to: next.endIso };
});
if (shellPeriod) {
  filters.value = {
    ...filters.value,
    from: shellPeriod.period.value.startIso,
    to: shellPeriod.period.value.endIso,
  };
}
const compareData = ref(null);
// Phase 2.5 I: resumos recentes
const recentSummaries = ref([]);
const summariesFilter = ref({ sentiment: '', keyword_flagged_only: false });
// v650: Desempenho consolidado em Resumo / Equipe / Conversão
const activeSection = ref('resumo');
const sections = computed(() => [
  { id: 'resumo', label: t('VOIP_CALLS.ANALYTICS.SECTIONS.SUMMARY') },
  { id: 'equipe', label: t('VOIP_CALLS.ANALYTICS.SECTIONS.TEAM') },
  { id: 'conversao', label: t('VOIP_CALLS.ANALYTICS.SECTIONS.CONVERSION') },
]);

function defaultFrom() {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
}

// Helpers pt-BR (mesmo pattern de VoipCallsList). filters.from/to continuam
// em ISO (backend inalterado); so a camada de display troca pra dd/mm/aaaa.
function formatBR(iso) {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}
function parseBR(str) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(str || '');
  if (!m) return null;
  const [, d, mo, y] = m;
  const dt = new Date(+y, +mo - 1, +d);
  if (dt.getFullYear() !== +y || dt.getMonth() !== +mo - 1 || dt.getDate() !== +d) return null;
  return `${y}-${mo}-${d}`;
}
function maskBR(val) {
  const digits = (val || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0,2)}/${digits.slice(2)}`;
  return `${digits.slice(0,2)}/${digits.slice(2,4)}/${digits.slice(4)}`;
}

const dateFromDisplay = ref(formatBR(filters.value.from));
const dateToDisplay   = ref(formatBR(filters.value.to));

function onDateInput(event, which) {
  const masked = maskBR(event.target.value);
  event.target.value = masked;
  if (which === 'from') dateFromDisplay.value = masked;
  else dateToDisplay.value = masked;

  const key = which === 'from' ? 'from' : 'to';
  if (!masked) { filters.value[key] = ''; return; }
  const iso = parseBR(masked);
  if (iso) filters.value[key] = iso;
  // Se parseia invalido, mantem display mas nao commita — load nao dispara.
}

async function settle(promise) {
  try { return { ok: true, data: (await promise).data }; }
  catch (e) { return { ok: false, error: e }; }
}

async function loadSettings() {
  try {
    const { data } = await VoipSettingsAPI.show();
    businessTypes.value = data.voip_business_types?.length ? data.voip_business_types : ['misto'];
  } catch (_e) { businessTypes.value = ['misto']; }
  // Phase 2.5 B: carrega disposition config pra mostrar o badge "alimentado por"
  // em cada metrica que depende de custom_field mapping.
  try { await store.dispatch('voipDisposition/fetchConfig'); } catch (_e) { /* ignore */ }
}

// Phase 2.5 B: reverse-lookup do mapping — dado um target, retorna o nome do
// custom field que alimenta ele. Usado no badge "alimentado por X".
const dispositionConfig = computed(() => store.getters['voipDisposition/getConfig']);
const customFieldMappings = computed(() => dispositionConfig.value?.custom_field_mappings || {});
const customFields = computed(() => dispositionConfig.value?.custom_fields || []);
function sourceForTarget(target) {
  const entry = Object.entries(customFieldMappings.value).find(([, m]) => m?.target === target);
  if (!entry) return null;
  const [key] = entry;
  const field = customFields.value.find(f => f.key === key);
  return { key, label: field?.label || key };
}

async function loadAll() {
  const requestId = ++loadRequestId;
  const isCurrent = () => requestId === loadRequestId;
  loading.value = true;
  loadError.value = null;
  try {
    const f = { ...filters.value };
    const [fu, fi, bo, ba, da, di, tg, cpl, att, ls, fc, chk] = await Promise.all([
      settle(VoipAnalyticsAPI.funnel(f)),
      settle(VoipAnalyticsAPI.financial(f)),
      settle(VoipAnalyticsAPI.byOrigin(f)),
      settle(VoipAnalyticsAPI.byAgent(f)),
      settle(VoipAnalyticsAPI.daily({ ...f, metric: 'leads' })),
      settle(VoipAnalyticsAPI.distance(f)),
      settle(VoipAnalyticsAPI.targets()),
      settle(VoipAnalyticsAPI.callsPerLead(f)),
      settle(VoipAnalyticsAPI.attendanceRate(f)),
      settle(VoipAnalyticsAPI.leadSpeed(f)),
      settle(VoipAnalyticsAPI.fcrRate(f)),
      settle(VoipAnalyticsAPI.setupChecklist()),
    ]);
    if (!isCurrent()) return;
    funnel.value    = fu.ok ? fu.data : null;
    financial.value = fi.ok ? fi.data : null;
    funnelFailed.value = !fu.ok;
    financialFailed.value = !fi.ok;
    byOrigin.value  = bo.ok ? bo.data : [];
    byAgent.value   = ba.ok ? ba.data : [];
    daily.value     = da.ok ? da.data : [];
    distance.value  = di.ok ? di.data : [];
    targets.value   = tg.ok ? tg.data : null;
    callsPerLead.value = cpl.ok ? cpl.data : null;
    attendance.value   = att.ok ? att.data : null;
    leadSpeed.value    = ls.ok ? ls.data : null;
    fcr.value          = fc.ok ? fc.data : null;
    checklist.value    = chk.ok ? chk.data : { items: [], done: 0, total: 0, pct: null, failed: true };

    // Phase 2.5 I: busca separada pra nao bloquear render principal
    try {
      const r = await VoipAnalyticsAPI.recentSummaries({ limit: 10, ...summariesFilter.value });
      if (isCurrent()) recentSummaries.value = r.data || [];
    } catch (_e) { if (isCurrent()) recentSummaries.value = []; }
    if (!isCurrent()) return;

    const failed = [fu, fi, bo, ba, da, di, tg, cpl, att, ls, fc, chk].filter(r => !r.ok);
    if (failed.length) {
      loadError.value =
        failed.length === 1
          ? t('VOIP_CALLS.ANALYTICS.LOAD_ERROR_ONE')
          : t('VOIP_CALLS.ANALYTICS.LOAD_ERROR_MANY', { count: failed.length });
      failed.forEach(r => console.error('[VoipAnalytics]', r.error));
    }
  } finally {
    if (isCurrent()) loading.value = false;
  }
}

onMounted(async () => {
  await loadSettings();
  await loadAll();
});
watch(filters, loadAll, { deep: true });

// Carrega comparacao sob demanda (toggle ativo + filtros com from+to)
watch([compareEnabled, filters], async () => {
  const requestId = ++compareRequestId;
  if (!compareEnabled.value || !filters.value.from || !filters.value.to) {
    compareData.value = null;
    return;
  }
  try {
    const { data } = await VoipAnalyticsAPI.compare({ ...filters.value });
    if (requestId === compareRequestId) compareData.value = data;
  } catch (_e) { if (requestId === compareRequestId) compareData.value = null; }
}, { deep: true });

// Na casa, "Comparar com o periodo anterior" e a caixa do seletor unico
// (?compare=1). Vem depois do watch acima para a comparacao carregar ja na
// abertura quando o link traz compare=1.
if (shellPeriod) {
  watch(
    () => route?.query?.compare === '1',
    value => {
      compareEnabled.value = value;
    },
    { immediate: true }
  );
}

// Helper: calcula delta % entre current e previous
function deltaPct(current, previous) {
  if (!previous || previous === 0) return null;
  return (((current - previous) / previous) * 100).toFixed(1);
}
function deltaLabel(current, previous) {
  const d = deltaPct(current, previous);
  if (d === null) return '';
  const num = parseFloat(d);
  if (num > 0) return `↑ +${d}%`;
  if (num < 0) return `↓ ${d}%`;
  return '= 0%';
}
function deltaClass(current, previous) {
  const d = deltaPct(current, previous);
  if (d === null) return 'text-n-slate-11';
  return parseFloat(d) >= 0 ? 'text-n-teal-11' : 'text-n-ruby-11';
}

// Visibilidade de bloco: retorna true se algum tipo em `types` bate com os tipos
// selecionados pelo usuario. 'misto' sempre bate com tudo.
function visible(allowedTypes) {
  const active = businessTypes.value;
  if (active.includes('misto')) return true;
  return allowedTypes.some(t => active.includes(t));
}

const hasAnyData = computed(() => {
  return (funnel.value?.leads || 0) > 0 ||
         (financial.value?.valor_orcado_cents || 0) > 0 ||
         (callsPerLead.value?.total_leads || 0) > 0 ||
         byOrigin.value.length > 0 ||
         byAgent.value.length > 0 ||
         distance.value.length > 0;
});

function formatBRL(cents) { return formatarCentavos(cents); }
function formatPct(p) { return `${(p ?? 0).toFixed(2)}%`; }
function formatDays(d) {
  return d == null ? '-' : t('VOIP_CALLS.ANALYTICS.DAYS', { count: d.toFixed(1) });
}

// Labels dos tipos ativos pra header
const OPERATION_TYPE_KEYS = {
  agendamentos: 'SCHEDULING',
  vendas: 'SALES',
  suporte: 'SUPPORT',
  sdr_bdr: 'SDR_BDR',
  misto: 'MIXED',
};
const activeTypesLabel = computed(() =>
  businessTypes.value
    .map(type =>
      OPERATION_TYPE_KEYS[type]
        ? t(`VOIP_CALLS.ANALYTICS.OPERATION_TYPES.${OPERATION_TYPE_KEYS[type]}`)
        : type
    )
    .join(' · ')
);

// Metas do mês: o bloco lia VoipMonthlyTarget, mas nenhuma tela gravava,
// então toda meta era 0. O administrador edita aqui mesmo.
const editingTargets = ref(false);
const targetDraft = ref({});
const savingTargets = ref(false);
const targetsError = ref('');
const targetsHaveValues = computed(() => hasAnyTarget(targets.value?.kpis));
const targetRows = computed(() =>
  TARGET_FIELDS.filter(field => targets.value?.kpis?.[field.kpi]).map(field => ({
    ...field,
    kpi: targets.value.kpis[field.kpi],
  }))
);
const targetsMonthLabel = computed(() => {
  const month = targets.value?.month;
  if (!month) return '';
  const date = new Date(`${month.slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime())
    ? month
    : date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
});
function formatTargetValue(field, value) {
  return field.money ? formatBRL(value) : value;
}
function startEditTargets() {
  targetDraft.value = targetDraftFromKpis(targets.value?.kpis);
  targetsError.value = '';
  editingTargets.value = true;
}
function cancelEditTargets() {
  editingTargets.value = false;
  targetsError.value = '';
}
async function saveTargets() {
  if (savingTargets.value || !targets.value?.month) return;
  savingTargets.value = true;
  targetsError.value = '';
  try {
    await VoipAnalyticsAPI.updateTargets({
      month: targets.value.month,
      label: targets.value.label,
      ...targetPayloadFromDraft(targetDraft.value),
    });
    const { data } = await VoipAnalyticsAPI.targets();
    targets.value = data;
    editingTargets.value = false;
  } catch {
    targetsError.value = t('VOIP_CALLS.ANALYTICS.TARGETS.SAVE_ERROR');
  } finally {
    savingTargets.value = false;
  }
}
</script>

<template>
  <div
    class="w-full bg-n-surface-1"
    :class="embedded ? '' : 'h-full overflow-y-auto'"
  >
    <header
      class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-4 bg-n-solid-1"
      :class="embedded ? '' : 'px-4 sm:px-6 border-b border-n-weak'"
    >
      <div>
        <h1 v-if="!embedded" class="text-xl font-medium text-n-slate-12">{{ t('VOIP_CALLS.ANALYTICS.TITLE') }}</h1>
        <p class="text-xs text-n-slate-11 mt-0.5">
          {{ t('VOIP_CALLS.ANALYTICS.OPERATION') }} <span class="font-medium text-n-slate-12">{{ activeTypesLabel }}</span>
          <template v-if="isAdmin">
            · <router-link :to="{ name: 'voip_calls_settings', query: { tab: 'operacao' } }" data-testid="voip-analytics-change-operation" class="text-n-brand hover:underline">{{ t('VOIP_CALLS.ANALYTICS.CHANGE_OPERATION') }}</router-link>
          </template>
        </p>
      </div>
      <div class="flex items-center gap-1 rounded-xl border border-n-weak bg-n-solid-1 p-1">
        <button
          v-for="section in sections"
          :key="section.id"
          type="button"
          class="min-h-11 rounded-md px-3 text-xs font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
          :aria-pressed="activeSection === section.id ? 'true' : 'false'"
          :class="
            activeSection === section.id
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1'
          "
          @click="activeSection = section.id"
        >
          {{ section.label }}
        </button>
      </div>
    </header>

    <!-- Embutida nos relatorios, a casca ja da a margem lateral: sem o padding
         proprio a tela nao soma 32px de cada lado no celular. -->
    <div
      class="space-y-5 pb-16 max-w-7xl mx-auto"
      :class="embedded ? 'py-4' : 'p-4 sm:p-6'"
    >
      <!-- Layer 4+5: Checklist de setup (somente se tiver step pendente) -->
      <section
        v-if="checklist.failed"
        class="rounded-2xl border border-n-weak bg-n-solid-1 p-4"
        data-testid="voip-checklist-failed"
      >
        <p class="text-sm text-n-slate-11">
          {{ t('VOIP_CALLS.ANALYTICS.CHECKLIST.FAILED') }}
        </p>
      </section>

      <section v-else-if="checklistIncomplete"
               class="rounded-2xl border border-n-amber-7 bg-n-amber-2 p-4"
               data-testid="voip-checklist">
        <button type="button"
                class="w-full min-h-11 flex items-center justify-between gap-3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand rounded-xl"
                :aria-expanded="checklistCollapsed ? 'false' : 'true'"
                @click="checklistCollapsed = !checklistCollapsed">
          <span class="flex items-center gap-3">
            <span class="i-ph-clipboard-text size-5 shrink-0 text-n-amber-11" aria-hidden="true" />
            <span>
              <span class="block text-sm font-semibold text-n-amber-11">
                {{ t('VOIP_CALLS.ANALYTICS.CHECKLIST.INCOMPLETE', { done: checklist.done, total: checklist.total }) }}
              </span>
              <span class="flex items-center gap-2 mt-1">
                <span class="block h-1.5 w-32 rounded bg-n-amber-4 overflow-hidden">
                  <span class="block h-full bg-n-amber-9" :style="{ width: `${checklist.pct}%` }" />
                </span>
                <span class="text-[11px] text-n-amber-11">{{ checklist.pct }}%</span>
              </span>
            </span>
          </span>
          <span class="flex items-center gap-1 text-xs text-n-amber-11">
            <span :class="[checklistCollapsed ? 'i-ph-caret-down' : 'i-ph-caret-up', 'size-3.5']" aria-hidden="true" />
            {{ checklistCollapsed ? t('VOIP_CALLS.ANALYTICS.CHECKLIST.EXPAND') : t('VOIP_CALLS.ANALYTICS.CHECKLIST.COLLAPSE') }}
          </span>
        </button>
        <ul v-show="!checklistCollapsed" class="mt-4 space-y-2">
          <li v-for="item in checklist.items" :key="item.id"
              class="flex items-start gap-2 text-sm">
            <span
              :class="[item.done ? 'i-ph-check-circle text-n-teal-11' : 'i-ph-hourglass text-n-amber-11', 'size-5 shrink-0']"
              aria-hidden="true"
            />
            <span class="sr-only">{{ item.done ? t('VOIP_CALLS.ANALYTICS.CHECKLIST.DONE') : t('VOIP_CALLS.ANALYTICS.CHECKLIST.PENDING') }}</span>
            <div class="flex-1 min-w-0">
              <p :class="item.done ? 'text-n-slate-11 line-through' : 'text-n-slate-12 font-medium'">
                {{ item.label }}
              </p>
              <p v-if="!item.done" class="text-[11px] text-n-slate-11 mt-0.5">
                {{ item.hint }}
                <router-link v-if="item.route && (isAdmin || item.route !== 'voip_calls_settings')"
                             :to="{ name: item.route }"
                             class="inline-flex items-center gap-0.5 text-n-brand underline ml-1">
                  {{ t('VOIP_CALLS.ANALYTICS.CHECKLIST.OPEN') }}
                  <span class="i-ph-arrow-right size-3" aria-hidden="true" />
                </router-link>
                <span v-else-if="item.route" data-testid="voip-checklist-ask-admin" class="ml-1">
                  {{ t('VOIP_CALLS.ANALYTICS.ASK_ADMIN') }}
                </span>
              </p>
            </div>
          </li>
        </ul>
      </section>

      <!-- Filtros -->
      <section
        v-if="!shellPeriod"
        class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 space-y-3"
      >
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label for="voip-analytics-from" class="block text-[10px] font-medium text-n-slate-11 uppercase mb-1">{{ t('VOIP_CALLS.ANALYTICS.FILTERS.FROM') }}</label>
            <input id="voip-analytics-from" :value="dateFromDisplay" type="text" inputmode="numeric" maxlength="10" :placeholder="t('VOIP_CALLS.ANALYTICS.FILTERS.DATE_PLACEHOLDER')"
                   class="w-full rounded-xl border border-n-weak bg-n-alpha-1 px-2 py-1.5 text-sm text-n-slate-12 tabular-nums"
                   @input="onDateInput($event, 'from')" />
          </div>
          <div>
            <label for="voip-analytics-to" class="block text-[10px] font-medium text-n-slate-11 uppercase mb-1">{{ t('VOIP_CALLS.ANALYTICS.FILTERS.TO') }}</label>
            <input id="voip-analytics-to" :value="dateToDisplay" type="text" inputmode="numeric" maxlength="10" :placeholder="t('VOIP_CALLS.ANALYTICS.FILTERS.DATE_PLACEHOLDER')"
                   class="w-full rounded-xl border border-n-weak bg-n-alpha-1 px-2 py-1.5 text-sm text-n-slate-12 tabular-nums"
                   @input="onDateInput($event, 'to')" />
          </div>
        </div>
        <label class="inline-flex min-h-11 items-center gap-2 text-xs text-n-slate-12">
          <input v-model="compareEnabled" type="checkbox" class="rounded border-n-weak" />
          <span>{{ t('VOIP_CALLS.ANALYTICS.FILTERS.COMPARE') }}</span>
        </label>
      </section>

      <div v-if="loading" class="p-8 text-center text-sm text-n-slate-11">{{ t('VOIP_CALLS.ANALYTICS.LOADING') }}</div>

      <div v-if="loadError" role="alert" class="rounded-xl border border-n-ruby-7 bg-n-ruby-2 p-3 text-sm text-n-ruby-11 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <span class="flex items-center gap-1.5">
          <span class="i-ph-warning size-4 shrink-0" aria-hidden="true" />
          {{ loadError }}
        </span>
        <button
          type="button"
          class="min-h-11 rounded-xl border border-n-ruby-7 px-3 text-xs font-medium text-n-ruby-11 hover:bg-n-ruby-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
          @click="loadAll"
        >
          {{ t('VOIP_CALLS.ANALYTICS.RETRY') }}
        </button>
      </div>

      <!-- Com grupo em falha, "sem chamadas" seria falso: o banner já avisa. -->
      <div v-if="!loading && !loadError && !hasAnyData" class="rounded-2xl border border-dashed border-n-weak bg-n-solid-1 p-8 text-center">
        <p class="text-sm text-n-slate-11">{{ t('VOIP_CALLS.ANALYTICS.EMPTY_TITLE') }}</p>
        <p class="text-sm text-n-slate-11">{{ t('VOIP_CALLS.ANALYTICS.EMPTY_DESCRIPTION') }}</p>
      </div>

      <div v-if="!loading && (activeSection === 'resumo' || activeSection === 'conversao') && funnelFailed && visible(['agendamentos','vendas','sdr_bdr'])"
           data-testid="voip-funnel-failed"
           class="rounded-2xl border border-dashed border-n-ruby-7 bg-n-solid-1 p-4 text-sm text-n-ruby-11">
        {{ t('VOIP_CALLS.ANALYTICS.FUNNEL_FAILED') }}
      </div>

      <!-- === BLOCO: Funnel KPIs (todos os tipos que tem fluxo) === -->
      <section v-if="(activeSection === 'resumo' || activeSection === 'conversao') && funnel && visible(['agendamentos','vendas','sdr_bdr'])" class="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="voip-funnel">
        <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
          <p class="text-xs text-n-slate-11 uppercase">{{ t('VOIP_CALLS.ANALYTICS.FUNNEL.LEADS') }}</p>
          <p class="text-2xl font-extrabold text-n-slate-12 mt-1">{{ funnel.leads }}</p>
          <p v-if="compareData?.previous && deltaLabel(funnel.leads, compareData.previous.funnel.leads)"
             :class="['text-[11px] mt-0.5', deltaClass(funnel.leads, compareData.previous.funnel.leads)]">
            {{ t('VOIP_CALLS.ANALYTICS.VS_PREVIOUS', { delta: deltaLabel(funnel.leads, compareData.previous.funnel.leads) }) }}
          </p>
        </div>
        <div v-if="visible(['agendamentos'])" class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
          <p class="text-xs text-n-slate-11 uppercase">{{ t('VOIP_CALLS.ANALYTICS.FUNNEL.SCHEDULED') }}</p>
          <p class="text-2xl font-extrabold text-n-slate-12 mt-1">
            {{ funnel.total_agendamentos }}
            <span class="text-xs font-normal text-n-slate-11 ml-1">({{ formatPct(funnel.pct_agendamentos) }})</span>
          </p>
        </div>
        <div v-if="visible(['agendamentos'])" class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
          <p class="text-xs text-n-slate-11 uppercase">{{ t('VOIP_CALLS.ANALYTICS.FUNNEL.ATTENDED') }}</p>
          <p class="text-2xl font-extrabold text-n-teal-11 mt-1">
            {{ funnel.comparecimentos }}
            <span class="text-xs font-normal text-n-slate-11 ml-1">({{ formatPct(funnel.pct_comparecimentos) }})</span>
          </p>
        </div>
        <div v-if="visible(['vendas','agendamentos'])" class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
          <p class="text-xs text-n-slate-11 uppercase">{{ t('VOIP_CALLS.ANALYTICS.FUNNEL.CLOSED') }}</p>
          <p class="text-2xl font-extrabold text-n-brand mt-1">
            {{ funnel.fechamentos }}
            <span class="text-xs font-normal text-n-slate-11 ml-1">({{ formatPct(funnel.pct_fechamentos) }})</span>
          </p>
        </div>
      </section>

      <!-- Layer 4: alerta se financial zerado mas há setup pendente -->
      <div v-if="(activeSection === 'resumo' || activeSection === 'conversao') && financial && visible(['vendas']) && !financial.valor_fechado_cents && !sourceForTarget('fechamento_cents')"
           data-testid="voip-financial-setup-hint"
           class="rounded-xl border border-n-amber-7 bg-n-amber-2 p-3 text-sm text-n-amber-11">
        <p class="flex items-start gap-1.5">
          <span class="i-ph-warning size-4 shrink-0 mt-0.5" aria-hidden="true" />
          <span><strong>{{ t('VOIP_CALLS.ANALYTICS.FINANCIAL_HINT.TITLE') }}</strong> {{ t('VOIP_CALLS.ANALYTICS.FINANCIAL_HINT.DESCRIPTION') }}</span>
        </p>
        <p v-if="isAdmin" class="text-[11px] mt-1">
          {{ t('VOIP_CALLS.ANALYTICS.FINANCIAL_HINT.CONFIGURE_PREFIX') }}
          <router-link :to="{ name: 'voip_calls_settings', query: { tab: 'finalizacao' } }" class="underline">{{ t('VOIP_CALLS.ANALYTICS.FINANCIAL_HINT.CONFIGURE_LINK') }}</router-link>.
          {{ t('VOIP_CALLS.ANALYTICS.FINANCIAL_HINT.CONFIGURE_HELP') }}
        </p>
        <p v-else class="text-[11px] mt-1">{{ t('VOIP_CALLS.ANALYTICS.ASK_ADMIN') }}</p>
      </div>

      <div v-if="!loading && (activeSection === 'resumo' || activeSection === 'conversao') && financialFailed && visible(['vendas'])"
           data-testid="voip-financial-failed"
           class="rounded-2xl border border-dashed border-n-ruby-7 bg-n-solid-1 p-4 text-sm text-n-ruby-11">
        {{ t('VOIP_CALLS.ANALYTICS.FINANCIAL_FAILED') }}
      </div>

      <!-- === BLOCO: Financial (vendas) === -->
      <section v-if="(activeSection === 'resumo' || activeSection === 'conversao') && financial && visible(['vendas'])" class="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
          <div class="flex items-center justify-between">
            <p class="text-xs text-n-slate-11 uppercase">{{ t('VOIP_CALLS.ANALYTICS.FINANCIAL.BUDGETED') }}</p>
            <span v-if="sourceForTarget('orcamento_cents')"
                  class="i-ph-link size-3.5 text-n-brand"
                  role="img"
                  :aria-label="t('VOIP_CALLS.ANALYTICS.FINANCIAL.FED_BY', { field: sourceForTarget('orcamento_cents').label })"
                  :title="t('VOIP_CALLS.ANALYTICS.FINANCIAL.FED_BY', { field: sourceForTarget('orcamento_cents').label })" />
          </div>
          <p class="text-lg font-medium text-n-slate-12 mt-1">{{ formatBRL(financial.valor_orcado_cents) }}</p>
        </div>
        <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
          <div class="flex items-center justify-between">
            <p class="text-xs text-n-slate-11 uppercase">{{ t('VOIP_CALLS.ANALYTICS.FINANCIAL.CLOSED') }}</p>
            <span v-if="sourceForTarget('fechamento_cents')"
                  class="i-ph-link size-3.5 text-n-brand"
                  role="img"
                  :aria-label="t('VOIP_CALLS.ANALYTICS.FINANCIAL.FED_BY', { field: sourceForTarget('fechamento_cents').label })"
                  :title="t('VOIP_CALLS.ANALYTICS.FINANCIAL.FED_BY', { field: sourceForTarget('fechamento_cents').label })" />
          </div>
          <p class="text-lg font-medium text-n-teal-11 mt-1">{{ formatBRL(financial.valor_fechado_cents) }}</p>
        </div>
        <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
          <p class="text-xs text-n-slate-11 uppercase">{{ t('VOIP_CALLS.ANALYTICS.FINANCIAL.AVG_TICKET') }}</p>
          <p class="text-lg font-medium text-n-slate-12 mt-1">{{ formatBRL(financial.ticket_medio_cents) }}</p>
        </div>
        <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
          <p class="text-xs text-n-slate-11 uppercase">{{ t('VOIP_CALLS.ANALYTICS.FINANCIAL.SALES_CONVERSION') }}</p>
          <p class="text-lg font-medium text-n-slate-12 mt-1">{{ formatPct(financial.taxa_conversao_vendas) }}</p>
        </div>
      </section>

      <!-- Layer 4: alerta se attendance está zero mas há setup pendente -->
      <div v-if="activeSection === 'resumo' && attendance && visible(['agendamentos']) && attendance.total_past === 0"
           class="rounded-xl border border-n-amber-7 bg-n-amber-2 p-3 text-sm text-n-amber-11">
        <p class="flex items-start gap-1.5">
          <span class="i-ph-warning size-4 shrink-0 mt-0.5" aria-hidden="true" />
          <span><strong>{{ t('VOIP_CALLS.ANALYTICS.ATTENDANCE_HINT.TITLE') }}</strong> {{ t('VOIP_CALLS.ANALYTICS.ATTENDANCE_HINT.DESCRIPTION') }}</span>
        </p>
        <p class="text-[11px] mt-1">{{ t('VOIP_CALLS.ANALYTICS.ATTENDANCE_HINT.HELP') }}</p>
      </div>

      <!-- === BLOCO: Comparecimento (agendamentos) === -->
      <section v-if="(activeSection === 'resumo' || activeSection === 'conversao') && attendance && visible(['agendamentos'])" class="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
          <p class="text-xs text-n-slate-11 uppercase">{{ t('VOIP_CALLS.ANALYTICS.ATTENDANCE.RATE') }}</p>
          <p class="text-lg font-medium text-n-teal-11 mt-1">{{ formatPct(attendance.attendance_rate) }}</p>
          <p class="text-[11px] text-n-slate-11 mt-1">{{ t('VOIP_CALLS.ANALYTICS.ATTENDANCE.RATE_HELP', { attended: attendance.attended, total: attendance.attended + attendance.no_show + attendance.pending_status }) }}</p>
        </div>
        <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
          <p class="text-xs text-n-slate-11 uppercase">{{ t('VOIP_CALLS.ANALYTICS.ATTENDANCE.NO_SHOW_RATE') }}</p>
          <p class="text-lg font-medium text-n-ruby-11 mt-1">{{ formatPct(attendance.no_show_rate) }}</p>
          <p class="text-[11px] text-n-slate-11 mt-1">{{ t('VOIP_CALLS.ANALYTICS.ATTENDANCE.NO_SHOW_HELP', { count: attendance.no_show }) }}</p>
        </div>
        <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
          <p class="text-xs text-n-slate-11 uppercase">{{ t('VOIP_CALLS.ANALYTICS.ATTENDANCE.CANCELLATIONS') }}</p>
          <p class="text-lg font-medium text-n-slate-12 mt-1">{{ formatPct(attendance.cancellation_rate) }}</p>
          <p class="text-[11px] text-n-slate-11 mt-1">{{ t('VOIP_CALLS.ANALYTICS.ATTENDANCE.CANCELLATIONS_HELP', { count: attendance.cancelled }) }}</p>
        </div>
        <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
          <p class="text-xs text-n-slate-11 uppercase">{{ t('VOIP_CALLS.ANALYTICS.ATTENDANCE.PENDING') }}</p>
          <p class="text-lg font-medium text-n-amber-11 mt-1">{{ attendance.pending_status }}</p>
          <p class="text-[11px] text-n-slate-11 mt-1">{{ t('VOIP_CALLS.ANALYTICS.ATTENDANCE.PENDING_HELP') }}</p>
        </div>
      </section>

      <!-- === BLOCO: FCR (suporte) === -->
      <section v-if="activeSection === 'resumo' && fcr && visible(['suporte'])" class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
        <h2 class="text-sm font-semibold text-n-slate-12 mb-3">{{ t('VOIP_CALLS.ANALYTICS.FCR.TITLE') }}</h2>
        <div v-if="fcr.needs_setup" data-testid="voip-fcr-setup" class="rounded-xl border border-dashed border-n-amber-7 bg-n-amber-2 p-4 text-sm text-n-amber-11">
          <p class="flex items-start gap-1.5">
            <span class="i-ph-gear size-4 shrink-0 mt-0.5" aria-hidden="true" />
            <span>
              {{ t('VOIP_CALLS.ANALYTICS.FCR.NEEDS_SETUP') }}
              <router-link v-if="isAdmin" :to="{ name: 'voip_calls_settings', query: { tab: 'finalizacao' } }" class="underline">{{ t('VOIP_CALLS.ANALYTICS.FCR.SETUP_LINK') }}</router-link>
              <template v-else>{{ t('VOIP_CALLS.ANALYTICS.ASK_ADMIN') }}</template>
            </span>
          </p>
          <p class="text-[11px] mt-1">{{ t('VOIP_CALLS.ANALYTICS.FCR.HELP') }}</p>
        </div>
        <div v-else class="grid grid-cols-3 gap-3">
          <div class="rounded-xl bg-n-alpha-1 p-3">
            <p class="text-xs text-n-slate-11">{{ t('VOIP_CALLS.ANALYTICS.FCR.RATE') }}</p>
            <p class="text-2xl font-medium text-n-teal-11 mt-1">{{ formatPct(fcr.fcr_rate) }}</p>
          </div>
          <div class="rounded-xl bg-n-alpha-1 p-3">
            <p class="text-xs text-n-slate-11">{{ t('VOIP_CALLS.ANALYTICS.FCR.RESOLVED') }}</p>
            <p class="text-2xl font-extrabold text-n-slate-12 mt-1">{{ fcr.resolved }}</p>
          </div>
          <div class="rounded-xl bg-n-alpha-1 p-3">
            <p class="text-xs text-n-slate-11">{{ t('VOIP_CALLS.ANALYTICS.FCR.TOTAL') }}</p>
            <p class="text-2xl font-extrabold text-n-slate-12 mt-1">{{ fcr.total_with_call }}</p>
          </div>
        </div>
      </section>

      <!-- === BLOCO: Calls per lead (vendas/sdr/suporte) === -->
      <section v-if="(activeSection === 'resumo' || activeSection === 'conversao') && callsPerLead && visible(['vendas','sdr_bdr','suporte'])" class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
        <h2 class="text-sm font-semibold text-n-slate-12 mb-3">{{ t('VOIP_CALLS.ANALYTICS.CALLS_PER_LEAD.TITLE') }}</h2>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div class="rounded-xl bg-n-alpha-1 p-3">
            <p class="text-xs text-n-slate-11">{{ t('VOIP_CALLS.ANALYTICS.CALLS_PER_LEAD.AVERAGE') }}</p>
            <p class="text-2xl font-extrabold text-n-slate-12 mt-1">{{ callsPerLead.avg_calls }}</p>
          </div>
          <div class="rounded-xl bg-n-alpha-1 p-3">
            <p class="text-xs text-n-slate-11">{{ t('VOIP_CALLS.ANALYTICS.CALLS_PER_LEAD.MEDIAN') }}</p>
            <p class="text-2xl font-extrabold text-n-slate-12 mt-1">{{ callsPerLead.median_calls }}</p>
          </div>
          <div class="rounded-xl bg-n-alpha-1 p-3">
            <p class="text-xs text-n-slate-11">{{ t('VOIP_CALLS.ANALYTICS.CALLS_PER_LEAD.TO_CONVERT') }}</p>
            <p class="text-2xl font-extrabold text-n-teal-11 mt-1">{{ callsPerLead.avg_calls_to_convert }}</p>
            <p class="text-[11px] text-n-slate-11">{{ t('VOIP_CALLS.ANALYTICS.CALLS_PER_LEAD.CONVERTED', { count: callsPerLead.converted_leads }) }}</p>
          </div>
          <div class="rounded-xl bg-n-alpha-1 p-3">
            <p class="text-xs text-n-slate-11">{{ t('VOIP_CALLS.ANALYTICS.CALLS_PER_LEAD.MAX') }}</p>
            <p class="text-2xl font-extrabold text-n-slate-12 mt-1">{{ callsPerLead.max_calls }}</p>
          </div>
        </div>
      </section>

      <!-- === BLOCO: Lead speed (agendamentos/vendas) === -->
      <section v-if="(activeSection === 'resumo' || activeSection === 'conversao') && leadSpeed && visible(['agendamentos','vendas','sdr_bdr'])" class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
        <h2 class="text-sm font-semibold text-n-slate-12 mb-3">{{ t('VOIP_CALLS.ANALYTICS.LEAD_SPEED.TITLE') }}</h2>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div v-if="visible(['agendamentos','sdr_bdr'])" class="rounded-xl bg-n-alpha-1 p-3">
            <p class="text-xs text-n-slate-11">{{ t('VOIP_CALLS.ANALYTICS.LEAD_SPEED.AVG_TO_SCHEDULE') }}</p>
            <p class="text-2xl font-extrabold text-n-slate-12 mt-1">{{ formatDays(leadSpeed.avg_days_to_schedule) }}</p>
            <p class="text-[11px] text-n-slate-11">{{ t('VOIP_CALLS.ANALYTICS.LEAD_SPEED.SCHEDULED', { count: leadSpeed.leads_scheduled }) }}</p>
          </div>
          <div v-if="visible(['agendamentos','sdr_bdr'])" class="rounded-xl bg-n-alpha-1 p-3">
            <p class="text-xs text-n-slate-11">{{ t('VOIP_CALLS.ANALYTICS.LEAD_SPEED.MEDIAN_TO_SCHEDULE') }}</p>
            <p class="text-2xl font-extrabold text-n-slate-12 mt-1">{{ formatDays(leadSpeed.median_days_to_schedule) }}</p>
          </div>
          <div v-if="visible(['vendas'])" class="rounded-xl bg-n-alpha-1 p-3">
            <p class="text-xs text-n-slate-11">{{ t('VOIP_CALLS.ANALYTICS.LEAD_SPEED.AVG_TO_CLOSE') }}</p>
            <p class="text-2xl font-extrabold text-n-teal-11 mt-1">{{ formatDays(leadSpeed.avg_days_to_close) }}</p>
            <p class="text-[11px] text-n-slate-11">{{ t('VOIP_CALLS.ANALYTICS.LEAD_SPEED.CLOSED', { count: leadSpeed.leads_closed }) }}</p>
          </div>
          <div v-if="visible(['vendas'])" class="rounded-xl bg-n-alpha-1 p-3">
            <p class="text-xs text-n-slate-11">{{ t('VOIP_CALLS.ANALYTICS.LEAD_SPEED.MEDIAN_TO_CLOSE') }}</p>
            <p class="text-2xl font-extrabold text-n-slate-12 mt-1">{{ formatDays(leadSpeed.median_days_to_close) }}</p>
          </div>
        </div>
      </section>

      <!-- === BLOCO: Metas (vendas/agendamentos) === -->
      <section v-if="(activeSection === 'resumo' || activeSection === 'conversao') && targets && visible(['vendas','agendamentos'])"
               data-testid="voip-targets"
               class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
        <header class="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h2 class="text-sm font-semibold text-n-slate-12">
            {{ t('VOIP_CALLS.ANALYTICS.TARGETS.TITLE', { month: targetsMonthLabel }) }}
          </h2>
          <button v-if="isAdmin && targetsHaveValues && !editingTargets"
                  type="button"
                  data-testid="voip-targets-edit"
                  class="inline-flex min-h-11 items-center gap-1 rounded-xl px-3 text-sm text-n-brand hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                  @click="startEditTargets">
            <span class="i-ph-pencil-simple size-4" aria-hidden="true" />
            {{ t('VOIP_CALLS.ANALYTICS.TARGETS.EDIT') }}
          </button>
        </header>

        <form v-if="editingTargets" data-testid="voip-targets-form" class="space-y-3" @submit.prevent="saveTargets">
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <div v-for="row in targetRows" :key="row.field">
              <label :for="`voip-target-${row.field}`" class="block text-xs text-n-slate-11 mb-1">
                {{ t(`VOIP_CALLS.ANALYTICS.TARGETS.KPIS.${row.key}`) }}<span v-if="row.money"> ({{ t('VOIP_CALLS.ANALYTICS.TARGETS.IN_REAIS') }})</span>
              </label>
              <input :id="`voip-target-${row.field}`"
                     v-model="targetDraft[row.field]"
                     :type="row.money ? 'text' : 'number'"
                     :inputmode="row.money ? 'decimal' : 'numeric'"
                     min="0"
                     step="1"
                     class="w-full min-h-11 rounded-xl border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12 tabular-nums" />
            </div>
          </div>
          <p v-if="targetsError" role="alert" class="text-sm text-n-ruby-11">{{ targetsError }}</p>
          <div class="flex flex-wrap gap-2">
            <button type="submit"
                    :disabled="savingTargets"
                    class="min-h-11 rounded-xl bg-n-brand px-4 text-sm font-medium text-white hover:bg-n-brand/90 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand">
              {{ savingTargets ? t('VOIP_CALLS.ANALYTICS.TARGETS.SAVING') : t('VOIP_CALLS.ANALYTICS.TARGETS.SAVE') }}
            </button>
            <button type="button"
                    :disabled="savingTargets"
                    class="min-h-11 rounded-xl border border-n-weak px-4 text-sm text-n-slate-12 hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                    @click="cancelEditTargets">
              {{ t('VOIP_CALLS.ANALYTICS.TARGETS.CANCEL') }}
            </button>
          </div>
        </form>

        <div v-else-if="!targetsHaveValues" data-testid="voip-targets-empty" class="rounded-xl border border-dashed border-n-weak p-4 text-sm text-n-slate-11 space-y-2">
          <p>{{ t('VOIP_CALLS.ANALYTICS.TARGETS.EMPTY') }}</p>
          <button v-if="isAdmin"
                  type="button"
                  data-testid="voip-targets-define"
                  class="inline-flex min-h-11 items-center gap-1 rounded-xl bg-n-brand px-4 text-sm font-medium text-white hover:bg-n-brand/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
                  @click="startEditTargets">
            <span class="i-ph-target size-4" aria-hidden="true" />
            {{ t('VOIP_CALLS.ANALYTICS.TARGETS.DEFINE') }}
          </button>
          <p v-else class="text-xs">{{ t('VOIP_CALLS.ANALYTICS.TARGETS.ASK_ADMIN') }}</p>
        </div>

        <div v-else class="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div v-for="row in targetRows" :key="row.field" class="rounded-xl bg-n-alpha-1 p-3">
            <p class="text-xs text-n-slate-11">{{ t(`VOIP_CALLS.ANALYTICS.TARGETS.KPIS.${row.key}`) }}</p>
            <div class="flex items-baseline justify-between gap-2 mt-1">
              <span class="text-xl font-extrabold text-n-slate-12">{{ formatTargetValue(row, row.kpi.current) }}</span>
              <span class="text-xs text-n-slate-11">{{ t('VOIP_CALLS.ANALYTICS.TARGETS.GOAL', { value: formatTargetValue(row, row.kpi.target) }) }}</span>
            </div>
            <div class="h-1.5 rounded bg-n-alpha-2 overflow-hidden mt-2">
              <div class="h-full bg-n-brand" :style="{ width: `${Math.min(row.kpi.pct, 100)}%` }" />
            </div>
            <p class="text-[10px] text-n-slate-11 mt-1">
              {{ t('VOIP_CALLS.ANALYTICS.TARGETS.PROGRESS', { pct: formatPct(row.kpi.pct), projection: formatTargetValue(row, row.kpi.projection), projectionPct: formatPct(row.kpi.projection_pct) }) }}
            </p>
          </div>
        </div>
      </section>
      <!-- === BLOCO: Por origem === -->
      <section v-if="(activeSection === 'resumo' || activeSection === 'conversao') && byOrigin?.length && visible(['vendas','agendamentos','sdr_bdr'])" class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 overflow-x-auto">
        <h2 class="text-sm font-semibold text-n-slate-12 mb-3">{{ t('VOIP_CALLS.ANALYTICS.BY_ORIGIN.TITLE') }}</h2>
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs text-n-slate-11 uppercase">
              <th class="py-2">{{ t('VOIP_CALLS.ANALYTICS.BY_ORIGIN.ORIGIN') }}</th>
              <th class="py-2 text-right">{{ t('VOIP_CALLS.ANALYTICS.BY_ORIGIN.LEADS') }}</th>
              <th class="py-2 text-right">{{ t('VOIP_CALLS.ANALYTICS.BY_ORIGIN.SHARE') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in byOrigin" :key="r.origin" class="border-t border-n-weak">
              <td class="py-2 text-n-slate-12">{{ r.origin }}</td>
              <td class="py-2 text-right tabular-nums">{{ r.leads }}</td>
              <td class="py-2 text-right tabular-nums">{{ formatPct(byOriginTotal ? (r.leads / byOriginTotal) * 100 : 0) }}</td>
            </tr>
          </tbody>
        </table>
      </section>
      <!-- === BLOCO: Por agente (todos) === -->
      <section v-if="activeSection === 'equipe' && byAgent?.length && visible(['vendas','agendamentos','suporte','sdr_bdr'])" class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 overflow-x-auto">
        <h2 class="text-sm font-semibold text-n-slate-12 mb-3">{{ t('VOIP_CALLS.ANALYTICS.BY_AGENT.TITLE') }}</h2>
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs text-n-slate-11 uppercase">
              <th class="py-2">{{ t('VOIP_CALLS.ANALYTICS.BY_AGENT.AGENT') }}</th>
              <th class="py-2 text-right">{{ t('VOIP_CALLS.ANALYTICS.BY_AGENT.LEADS') }}</th>
              <th class="py-2 text-right">{{ t('VOIP_CALLS.ANALYTICS.BY_AGENT.ANSWERED') }}</th>
              <th class="py-2 text-right">{{ t('VOIP_CALLS.ANALYTICS.BY_AGENT.SCHEDULED') }}</th>
              <th class="py-2 text-right" :title="t('VOIP_CALLS.ANALYTICS.BY_AGENT.GENERATED_HELP')">{{ t('VOIP_CALLS.ANALYTICS.BY_AGENT.GENERATED') }}</th>
              <th class="py-2 text-right" :title="t('VOIP_CALLS.ANALYTICS.BY_AGENT.REALIZED_HELP')">{{ t('VOIP_CALLS.ANALYTICS.BY_AGENT.REALIZED') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in byAgent" :key="r.agent_id" class="border-t border-n-weak">
              <td class="py-2 text-n-slate-12">{{ r.name }}</td>
              <td class="py-2 text-right tabular-nums">{{ r.leads }}</td>
              <td class="py-2 text-right tabular-nums">{{ r.answered }}</td>
              <td class="py-2 text-right tabular-nums">{{ r.agendamentos_count || 0 }}</td>
              <td class="py-2 text-right tabular-nums">{{ formatBRL(r.valor_gerado_cents) }}</td>
              <td class="py-2 text-right tabular-nums text-n-teal-11">{{ formatBRL(r.valor_realizado_cents) }}</td>
            </tr>
          </tbody>
        </table>
      </section>
      <div
        v-if="activeSection === 'equipe' && (!byAgent || !byAgent.length) && !loading"
        class="rounded-2xl border border-dashed border-n-weak bg-n-solid-1 p-8 text-center text-sm text-n-slate-11"
      >
        {{ t('VOIP_CALLS.ANALYTICS.BY_AGENT.EMPTY') }}
      </div>

      <!-- === BLOCO: Distância D1/D7/D30 (agendamentos) === -->
      <section v-if="activeSection === 'resumo' && distance?.length && visible(['agendamentos'])" class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 overflow-x-auto">
        <h2 class="text-sm font-semibold text-n-slate-12 mb-3">{{ t('VOIP_CALLS.ANALYTICS.DISTANCE.TITLE') }}</h2>
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs text-n-slate-11 uppercase">
              <th class="py-2">{{ t('VOIP_CALLS.ANALYTICS.DISTANCE.DAYS') }}</th>
              <th class="py-2 text-right">{{ t('VOIP_CALLS.ANALYTICS.DISTANCE.SCHEDULED') }}</th>
              <th class="py-2 text-right">{{ t('VOIP_CALLS.ANALYTICS.DISTANCE.ATTENDED') }}</th>
              <th class="py-2 text-right">{{ t('VOIP_CALLS.ANALYTICS.DISTANCE.ATTENDED_PCT') }}</th>
              <th class="py-2 text-right">{{ t('VOIP_CALLS.ANALYTICS.DISTANCE.DISTRIBUTION_PCT') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in distance" :key="d.distance_days" class="border-t border-n-weak">
              <td class="py-2">D{{ d.distance_days }}</td>
              <td class="py-2 text-right">{{ d.agendamentos }}</td>
              <td class="py-2 text-right">{{ d.comparecimentos }}</td>
              <td class="py-2 text-right">{{ formatPct(d.pct_comparecimento) }}</td>
              <td class="py-2 text-right">{{ formatPct(d.pct_distribuicao) }}</td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
  </div>
</template>
